import React, { useState, useMemo, useEffect, useRef } from "react";
import { Table, Row, Col, Form, InputGroup, Button, Badge, Modal, Alert, Spinner, Toast, ToastContainer, OverlayTrigger, Tooltip } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import axios from "axios";

// Raisons prédéfinies pour l'ajout de numéros stop
const raisonsPredefinies = [
  "Harcèlement téléphonique",
  "Spam commercial",
  "Réclamations abusives",
  "Numéro frauduleux",
  "Ancien employé",
  "Concurrent",
  "Test automatique",
  "Autre raison"
];

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const STOPS_ENDPOINT = `${API_BASE_URL}/stops`;

export default function StopNumbersManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [stopNumbers, setStopNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingNumber, setAddingNumber] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    type: 'success',
    title: '',
    message: '',
    icon: 'bi-check-circle'
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfig, setDeleteConfig] = useState({
    type: 'single',
    numberId: null,
    numero: '',
    count: 0,
    deleting: false
  });

  const [newStopNumber, setNewStopNumber] = useState({
    numero: '',
    nom: '',
    raison: '',
    notes: '',
    precisionAutre: ''
  });

  const initDoneRef = useRef(false);

  // ============================================
  // AUTH HEADERS
  // ============================================
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  };

  // ============================================
  // TOASTS
  // ============================================
  const showAlert = (type, title, message, icon = null) => {
    const icons = {
      success: 'bi-check-circle-fill',
      warning: 'bi-exclamation-triangle-fill',
      danger: 'bi-x-circle-fill',
      info: 'bi-info-circle-fill'
    };

    setToastConfig({
      type,
      title,
      message,
      icon: icon || icons[type] || 'bi-info-circle-fill'
    });
    setShowToast(true);

    setTimeout(() => setShowToast(false), 5000);
  };

  // ============================================
  // FETCH
  // ============================================
  const fetchStopNumbers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(STOPS_ENDPOINT, {
        headers: getAuthHeaders(),
        timeout: 15000,
        withCredentials: false
      });

      if (response.data && response.data.code === 200) {
        const apiData = response.data.data || [];

        const adaptedData = apiData.map((item) => ({
          id: item.id,
          numero: item.phone_number,
          nom: item.name || 'Non renseigné',
          raison: item.reason,
          dateAjout: item.created_at ? new Date(item.created_at).toLocaleString('fr-FR') : new Date().toLocaleString('fr-FR'),
          dateMiseAJour: item.updated_at ? new Date(item.updated_at).toLocaleString('fr-FR') : new Date().toLocaleString('fr-FR'),
          created_at: item.created_at,
          updated_at: item.updated_at,
          entite_id: item.entite_id,
          notes: item.notes || ''
        }));

        setStopNumbers(adaptedData);
      } else {
        throw new Error('Format de réponse API invalide');
      }
    } catch (error) {
      console.error('❌ Erreur chargement:', error);
      setStopNumbers([]);

      if (error.response && error.response.status === 404) {
        // Liste vide
      } else if (error.code === 'ERR_NETWORK') {
        // Erreur réseau
      } else {
        setError(`Erreur de chargement: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const addStopNumberAPI = async (stopData) => {
    const raisonComplete = stopData.raison === "Autre raison" && stopData.precisionAutre
      ? `Autre raison: ${stopData.precisionAutre}`
      : stopData.raison;

    const payload = {
      phone_number: stopData.numero,
      reason: raisonComplete,
      name: stopData.nom || 'Non renseigné',
      notes: stopData.notes || ''
    };

    const response = await axios.post(STOPS_ENDPOINT, payload, {
      headers: getAuthHeaders(),
      timeout: 10000,
      withCredentials: false
    });

    if (response.data && (response.data.code === 201 || response.data.code === 200)) {
      return response.data.data || response.data;
    } else {
      throw new Error('Réponse API invalide');
    }
  };

  const deleteStopNumberAPI = async (id) => {
    await axios.delete(`${STOPS_ENDPOINT}/${id}`, {
      headers: getAuthHeaders(),
      timeout: 10000,
      withCredentials: false
    });
    return true;
  };

  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    fetchStopNumbers();
  }, []);

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = stopNumbers.filter(item => {
      const matchesSearch =
        item.numero.includes(filterText) ||
        item.nom.toLowerCase().includes(filterText.toLowerCase()) ||
        item.raison.toLowerCase().includes(filterText.toLowerCase()) ||
        (item.notes && item.notes.toLowerCase().includes(filterText.toLowerCase()));
      return matchesSearch;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'created_at' || sortField === 'updated_at' || sortField === 'dateAjout' || sortField === 'dateMiseAJour') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [filterText, sortField, sortDirection, stopNumbers]);

  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const stats = useMemo(() => {
    const total = stopNumbers.length;
    const thisMonth = stopNumbers.filter(item => {
      if (!item.created_at) return false;
      const date = new Date(item.created_at);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length;
    const uniqueReasons = new Set(stopNumbers.map(i => i.raison)).size;

    return { total, thisMonth, uniqueReasons };
  }, [stopNumbers]);

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedNumbers(currentItems.map(item => item.id));
    else setSelectedNumbers([]);
  };

  const handleSelectNumber = (numberId) => {
    setSelectedNumbers(prev =>
      prev.includes(numberId)
        ? prev.filter(id => id !== numberId)
        : [...prev, numberId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedNumbers.length === currentItems.length;
  const isSomeSelected = selectedNumbers.length > 0 && selectedNumbers.length < currentItems.length;

  // ============================================
  // AJOUT
  // ============================================
  const handleAddStopNumber = async () => {
    if (!newStopNumber.numero.trim()) {
      showAlert('warning', 'Champ requis', 'Veuillez saisir un numéro de téléphone', 'bi-telephone');
      return;
    }
    if (!newStopNumber.raison.trim()) {
      showAlert('warning', 'Raison requise', 'Veuillez sélectionner une raison', 'bi-exclamation-triangle');
      return;
    }
    if (newStopNumber.raison === "Autre raison" && !newStopNumber.precisionAutre.trim()) {
      showAlert('warning', 'Précision requise', 'Veuillez préciser la raison', 'bi-exclamation-triangle');
      return;
    }

    try {
      setAddingNumber(true);

      const raisonComplete = newStopNumber.raison === "Autre raison" && newStopNumber.precisionAutre
        ? `Autre raison: ${newStopNumber.precisionAutre}`
        : newStopNumber.raison;

      await addStopNumberAPI({ ...newStopNumber, raison: raisonComplete });

      showAlert('success', 'Succès !', `Le numéro ${newStopNumber.numero} a été ajouté`, 'bi-shield-check');

      await fetchStopNumbers();
      setNewStopNumber({ numero: '', nom: '', raison: '', notes: '', precisionAutre: '' });
      setShowAddModal(false);
    } catch (error) {
      console.error('❌ Erreur ajout:', error);
      showAlert('danger', 'Erreur', `Impossible d'ajouter: ${error.message}`, 'bi-x-circle');
    } finally {
      setAddingNumber(false);
    }
  };

  // ============================================
  // SUPPRESSION
  // ============================================
  const showDeleteConfirmation = (type, numberId = null, numero = '', count = 0) => {
    setDeleteConfig({ type, numberId, numero, count, deleting: false });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (!deleteConfig.deleting) setShowDeleteModal(false);
  };

  const handleDeleteNumber = (numberId) => {
    const number = stopNumbers.find(item => item.id === numberId);
    showDeleteConfirmation('single', numberId, number?.numero, 1);
  };

  const handleBulkDelete = () => {
    if (selectedNumbers.length === 0) {
      showAlert('warning', 'Sélection requise', 'Sélectionnez au moins un numéro', 'bi-check-square');
      return;
    }
    showDeleteConfirmation('bulk', null, '', selectedNumbers.length);
  };

  const executeDelete = async () => {
    try {
      setDeleteConfig(prev => ({ ...prev, deleting: true }));

      if (deleteConfig.type === 'single') {
        const number = stopNumbers.find(item => item.id === deleteConfig.numberId);

        // ✅ Retirer immédiatement de l'UI (optimistic update)
        setStopNumbers(prev => prev.filter(item => item.id !== deleteConfig.numberId));

        await deleteStopNumberAPI(deleteConfig.numberId);

        showAlert('success', 'Supprimé', `Le numéro ${number?.numero} a été supprimé`, 'bi-trash');

        // ✅ Refresh en arrière-plan
        fetchStopNumbers();
      } else {
        const idsToDelete = [...selectedNumbers];

        // ✅ Retirer immédiatement de l'UI
        setStopNumbers(prev => prev.filter(item => !idsToDelete.includes(item.id)));
        setSelectedNumbers([]);

        const deletePromises = idsToDelete.map(id => deleteStopNumberAPI(id));
        await Promise.all(deletePromises);

        showAlert('success', 'Suppression groupée', `${idsToDelete.length} numéro(s) supprimé(s)`, 'bi-trash-fill');

        // ✅ Refresh en arrière-plan
        fetchStopNumbers();
      }

      setShowDeleteModal(false);
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      showAlert('danger', 'Erreur', `Impossible de supprimer: ${error.message}`, 'bi-x-circle');
      // ✅ Refresh pour restaurer si échec
      fetchStopNumbers();
      setDeleteConfig(prev => ({ ...prev, deleting: false }));
    }
  };

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedNumbers.length > 0
      ? stopNumbers.filter(item => selectedNumbers.includes(item.id))
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      alert("Aucune donnée à exporter");
      setExportLoading(false);
      return;
    }

    if (format === "csv") {
      const headers = ["Numéro", "Nom", "Raison", "Date Ajout", "Date Mise à jour", "Notes"];
      const csvContent = [
        "\uFEFF" + headers.join(";"),
        ...dataToExport.map(item => [
          item.numero, item.nom, item.raison, item.dateAjout, item.dateMiseAJour, item.notes
        ].map(v => `"${v}"`).join(";"))
      ].join("\n");
      downloadFile(csvContent, "text/csv;charset=utf-8;", `export-stop-numbers-${Date.now()}.csv`);
    } else if (format === "excel") {
      const ws = XLSX.utils.json_to_sheet(dataToExport.map(item => ({
        Numéro: item.numero, Nom: item.nom, Raison: item.raison,
        "Date Ajout": item.dateAjout, "Date Mise à jour": item.dateMiseAJour, Notes: item.notes
      })));
      ws['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 30 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Numéros Stop");
      XLSX.writeFile(wb, `export-stop-numbers-${Date.now()}.xlsx`);
    } else if (format === "json") {
      downloadFile(JSON.stringify(dataToExport, null, 2), "application/json;charset=utf-8;", `export-stop-numbers-${Date.now()}.json`);
    } else if (format === "pdf") {
      generatePDF(dataToExport);
    }

    setExportLoading(false);
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    const primaryColor = [41, 128, 185];
    const headerColor = [240, 240, 240];
    const alternateColor = [248, 249, 250];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 25, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("LISTE DES NUMÉROS STOP", 105, 15, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
    doc.text(`Total: ${data.length} numéro(s)`, 180, 22, { align: 'right' });

    const pageWidth = 210;
    const margin = 10;
    let yPosition = 35;

    const colWidths = [25, 25, 45, 35, 35];
    const headers = ["NUMÉRO", "NOM", "RAISON", "DATE AJOUT", "DATE MISE À JOUR"];

    doc.setFillColor(...headerColor);
    doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");

    let xPosition = margin;
    headers.forEach((header, index) => {
      doc.text(header, xPosition + 2, yPosition);
      xPosition += colWidths[index];
    });

    yPosition += 6;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");

    data.forEach((item, index) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        let newX = margin;
        headers.forEach((header, idx) => {
          doc.text(header, newX + 2, yPosition);
          newX += colWidths[idx];
        });
        yPosition += 6;
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
      }

      if (index % 2 === 0) {
        doc.setFillColor(...alternateColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 6, 'F');
      }

      const rowData = [
        item.numero,
        item.nom.length > 15 ? item.nom.substring(0, 15) + '...' : item.nom,
        item.raison.length > 20 ? item.raison.substring(0, 20) + '...' : item.raison,
        item.dateAjout,
        item.dateMiseAJour
      ];

      let currentX = margin;
      rowData.forEach((cell, cellIndex) => {
        doc.setTextColor(0, 0, 0);
        doc.text(cell, currentX + 2, yPosition);
        currentX += colWidths[cellIndex];
      });

      yPosition += 6;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 275, pageWidth - margin, 275);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    doc.save(`stop-numbers-${timestamp}.pdf`);
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="sn-sortable-header" onClick={() => handleSort(field)}>
      <span className="sn-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill sn-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  return (
    <div className="sn-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          bg={toastConfig.type}
          delay={5000}
          autohide
        >
          <Toast.Header className={`bg-${toastConfig.type} text-white`}>
            <i className={`bi ${toastConfig.icon} me-2`}></i>
            <strong className="me-auto">{toastConfig.title}</strong>
            <small>Maintenant</small>
          </Toast.Header>
          <Toast.Body className={toastConfig.type === 'warning' ? 'text-dark' : 'text-white'}>
            {toastConfig.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* ========== HEADER ========== */}
      <div className="sn-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="sn-header-icon">
              <i className="bi bi-shield-slash-fill"></i>
            </div>
            <div>
              <h3 className="sn-header-title mb-1">Numéros stop</h3>
              <p className="sn-header-subtitle mb-0">
                Liste noire des numéros bloqués pour toutes communications
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {selectedNumbers.length > 0 && (
              <Button className="sn-btn-danger" onClick={handleBulkDelete}>
                <i className="bi bi-trash"></i>
                <span>Supprimer ({selectedNumbers.length})</span>
              </Button>
            )}

            <button className="sn-btn-secondary" onClick={fetchStopNumbers} disabled={loading}>
              <i className={`bi ${loading ? 'bi-arrow-repeat sn-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>

            <div className="dropdown">
              <button
                className="sn-btn-secondary"
                type="button"
                data-bs-toggle="dropdown"
                disabled={exportLoading}
              >
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat sn-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </button>
              <ul className="dropdown-menu sn-dropdown-menu">
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('csv')}>
                    <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('excel')}>
                    <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('pdf')}>
                    <i className="bi bi-file-earmark-pdf text-danger me-2"></i>Export PDF
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('json')}>
                    <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                  </button>
                </li>
              </ul>
            </div>

            <button className="sn-btn-primary" onClick={() => setShowAddModal(true)}>
              <i className="bi bi-plus-circle"></i>
              <span>Ajouter un numéro</span>
            </button>
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && !loading && (
        <div className="sn-alert sn-alert-warning mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={4}>
          <div className="sn-stat-card sn-stat-red">
            <div className="sn-stat-icon-wrapper sn-stat-icon-red">
              <i className="bi bi-shield-slash-fill"></i>
            </div>
            <div className="sn-stat-content">
              <div className="sn-stat-label">Total numéros stop</div>
              <div className="sn-stat-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={4}>
          <div className="sn-stat-card sn-stat-amber">
            <div className="sn-stat-icon-wrapper sn-stat-icon-amber">
              <i className="bi bi-calendar-week-fill"></i>
            </div>
            <div className="sn-stat-content">
              <div className="sn-stat-label">Ajoutés ce mois</div>
              <div className="sn-stat-value">{stats.thisMonth.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={12} lg={4}>
          <div className="sn-stat-card sn-stat-blue">
            <div className="sn-stat-icon-wrapper sn-stat-icon-blue">
              <i className="bi bi-tags-fill"></i>
            </div>
            <div className="sn-stat-content">
              <div className="sn-stat-label">Raisons distinctes</div>
              <div className="sn-stat-value">{stats.uniqueReasons.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="sn-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="sn-search-wrapper">
              <i className="bi bi-search sn-search-icon"></i>
              <input
                type="text"
                className="sn-search-input"
                placeholder="Rechercher par numéro, nom, raison..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
                disabled={loading}
              />
              {filterText && (
                <button className="sn-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="sn-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              disabled={loading}
            >
              <option value={5}>5 par page</option>
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
            </select>
          </Col>
          <Col lg={3} md={6} className="text-md-end">
            <span className="sn-filter-info">
              <i className="bi bi-info-circle"></i>
              <strong>{filteredAndSortedItems.length}</strong> numéro{filteredAndSortedItems.length > 1 ? 's' : ''}
            </span>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="sn-table-card">
        {loading ? (
          <div className="sn-loading">
            <div className="sn-loading-spinner"></div>
            <div className="sn-loading-text">Chargement des numéros stop...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="sn-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="sn-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="sn-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <SortableHeader field="numero">
                      <i className="bi bi-telephone me-1"></i>Numéro
                    </SortableHeader>
                    <SortableHeader field="nom">
                      <i className="bi bi-person me-1"></i>Nom
                    </SortableHeader>
                    <SortableHeader field="raison">
                      <i className="bi bi-tag me-1"></i>Raison
                    </SortableHeader>
                    <SortableHeader field="dateAjout">
                      <i className="bi bi-calendar-plus me-1"></i>Date ajout
                    </SortableHeader>
                    <SortableHeader field="dateMiseAJour">
                      <i className="bi bi-arrow-repeat me-1"></i>Mise à jour
                    </SortableHeader>
                    <th className="text-end" style={{ width: '80px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item) => (
                      <tr key={item.id} className="sn-row">
                        <td>
                          <div className="sn-checkbox-wrapper">
                            <input
                              type="checkbox"
                              className="sn-checkbox"
                              checked={selectedNumbers.includes(item.id)}
                              onChange={() => handleSelectNumber(item.id)}
                            />
                          </div>
                        </td>
                        <td>
                          <code className="sn-code">{item.numero}</code>
                        </td>
                        <td>
                          <div className="sn-name-cell">
                            <div className="sn-avatar">
                              {item.nom?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <span className="sn-name-main">{item.nom}</span>
                          </div>
                        </td>
                        <td>
                          <span className="sn-reason-badge" title={item.raison}>
                            <i className="bi bi-shield-slash"></i>
                            {item.raison.length > 30 ? item.raison.substring(0, 30) + '…' : item.raison}
                          </span>
                        </td>
                        <td>
                          <span className="sn-date-cell">{item.dateAjout}</span>
                        </td>
                        <td>
                          <span className="sn-date-cell">{item.dateMiseAJour}</span>
                        </td>
                        <td className="text-end">
                          <div className="sn-actions">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                              <button
                                className="sn-action-btn sn-action-delete"
                                onClick={() => handleDeleteNumber(item.id)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7">
                        <div className="sn-empty-state">
                          <div className="sn-empty-icon">
                            <i className="bi bi-shield-check"></i>
                          </div>
                          <h5 className="sn-empty-title">Aucun numéro stop trouvé</h5>
                          <p className="sn-empty-text">
                            {filterText
                              ? "Aucun numéro ne correspond à votre recherche"
                              : "La liste des numéros stop est vide"}
                          </p>
                          {filterText ? (
                            <button className="sn-btn-empty-reset" onClick={() => setFilterText('')}>
                              <i className="bi bi-arrow-clockwise me-2"></i>
                              Réinitialiser
                            </button>
                          ) : (
                            <button className="sn-btn-empty-reset" onClick={() => setShowAddModal(true)}>
                              <i className="bi bi-plus-circle me-2"></i>
                              Ajouter un numéro
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="sn-pagination">
                <div className="sn-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                  {selectedNumbers.length > 0 && (
                    <span className="sn-pagination-selected">
                      • <strong>{selectedNumbers.length}</strong> sélectionné{selectedNumbers.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="sn-pagination-controls">
                  <button
                    className="sn-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button
                    className="sn-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`sn-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button
                    className="sn-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button
                    className="sn-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============================================
          MODAL AJOUT
          ============================================ */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered className="sn-modal">
        <div className="sn-modal-header sn-modal-header-danger">
          <div className="d-flex align-items-center gap-3">
            <div className="sn-modal-icon sn-modal-icon-danger">
              <i className="bi bi-shield-slash-fill"></i>
            </div>
            <div>
              <h5 className="sn-modal-title mb-0">Ajouter un numéro stop</h5>
              <small className="sn-modal-subtitle">Ce numéro sera bloqué</small>
            </div>
          </div>
          <button className="sn-modal-close" onClick={() => setShowAddModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="sn-modal-body">
          <div className="sn-warning-box mb-4">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <div>
              <strong>Attention</strong>
              <div className="small">Les numéros ajoutés seront bloqués pour toutes les communications.</div>
            </div>
          </div>

          <div className="sn-form-group">
            <label className="sn-form-label">
              Numéro de téléphone <span className="sn-required">*</span>
            </label>
            <input
              type="text"
              className="sn-input"
              placeholder="Ex: +24174151522 ou 24174151522"
              value={newStopNumber.numero}
              onChange={(e) => setNewStopNumber({ ...newStopNumber, numero: e.target.value })}
              disabled={addingNumber}
              autoFocus
            />
            <div className="sn-form-hint">
              <i className="bi bi-info-circle"></i>
              Format international (+241...) ou national (241...)
            </div>
          </div>

          <div className="sn-form-group">
            <label className="sn-form-label">
              Nom <span className="sn-optional">(optionnel)</span>
            </label>
            <input
              type="text"
              className="sn-input"
              placeholder="Nom associé au numéro"
              value={newStopNumber.nom}
              onChange={(e) => setNewStopNumber({ ...newStopNumber, nom: e.target.value })}
              disabled={addingNumber}
            />
          </div>

          <div className="sn-form-group">
            <label className="sn-form-label">
              Raison du blocage <span className="sn-required">*</span>
            </label>
            <select
              className="sn-select-full"
              value={newStopNumber.raison}
              onChange={(e) => setNewStopNumber({ ...newStopNumber, raison: e.target.value })}
              disabled={addingNumber}
            >
              <option value="">-- Sélectionner une raison --</option>
              {raisonsPredefinies.map((raison, index) => (
                <option key={index} value={raison}>{raison}</option>
              ))}
            </select>
          </div>

          {newStopNumber.raison === "Autre raison" && (
            <div className="sn-form-group">
              <label className="sn-form-label">
                Précisez la raison <span className="sn-required">*</span>
              </label>
              <input
                type="text"
                className="sn-input"
                placeholder="Veuillez préciser la raison..."
                value={newStopNumber.precisionAutre}
                onChange={(e) => setNewStopNumber({ ...newStopNumber, precisionAutre: e.target.value })}
                disabled={addingNumber}
              />
            </div>
          )}

          <div className="sn-form-group">
            <label className="sn-form-label">
              Notes <span className="sn-optional">(optionnel)</span>
            </label>
            <textarea
              className="sn-textarea"
              rows={2}
              placeholder="Notes complémentaires..."
              value={newStopNumber.notes}
              onChange={(e) => setNewStopNumber({ ...newStopNumber, notes: e.target.value })}
              disabled={addingNumber}
            />
          </div>
        </Modal.Body>

        <div className="sn-modal-footer">
          <button className="sn-btn-cancel" onClick={() => setShowAddModal(false)} disabled={addingNumber}>
            Annuler
          </button>
          <button
            className="sn-btn-danger"
            onClick={handleAddStopNumber}
            disabled={
              !newStopNumber.numero.trim() ||
              !newStopNumber.raison ||
              (newStopNumber.raison === "Autre raison" && !newStopNumber.precisionAutre.trim()) ||
              addingNumber
            }
          >
            {addingNumber ? (
              <>
                <Spinner animation="border" size="sm" />
                Ajout en cours...
              </>
            ) : (
              <>
                <i className="bi bi-shield-check"></i>
                Ajouter le numéro
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* ============================================
          MODAL SUPPRESSION
          ============================================ */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered className="sn-modal">
        <Modal.Body className="sn-delete-body">
          <div className="sn-delete-icon-wrapper">
            <div className="sn-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="sn-delete-title">Confirmer la suppression</h4>
          <p className="sn-delete-text">
            {deleteConfig.type === 'single'
              ? 'Êtes-vous sûr de vouloir supprimer ce numéro stop ?'
              : 'Êtes-vous sûr de vouloir supprimer les numéros sélectionnés ?'}
          </p>

          {deleteConfig.type === 'single' ? (
            <div className="sn-delete-item">
              <div className="sn-delete-item-icon">
                <i className="bi bi-telephone-x"></i>
              </div>
              <div className="sn-delete-item-content">
                <div className="sn-delete-item-label">Numéro</div>
                <div className="sn-delete-item-value">{deleteConfig.numero}</div>
              </div>
            </div>
          ) : (
            <div className="sn-delete-item">
              <div className="sn-delete-item-icon">
                <i className="bi bi-trash-fill"></i>
              </div>
              <div className="sn-delete-item-content">
                <div className="sn-delete-item-label">Numéros sélectionnés</div>
                <div className="sn-delete-item-value">{deleteConfig.count} numéro(s)</div>
              </div>
            </div>
          )}

          <div className="sn-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible
          </div>

          <div className="sn-delete-actions">
            <button
              className="sn-btn-cancel sn-btn-flex"
              onClick={closeDeleteModal}
              disabled={deleteConfig.deleting}
            >
              <i className="bi bi-x-lg"></i>
              Annuler
            </button>
            <button
              className="sn-btn-danger sn-btn-flex"
              onClick={executeDelete}
              disabled={deleteConfig.deleting}
            >
              {deleteConfig.deleting ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Suppression...
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  Supprimer
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .sn-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .sn-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .sn-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .sn-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(239, 68, 68, 0.5);
        }
        .sn-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .sn-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* ========== BUTTONS ========== */
        .sn-btn-primary, .sn-btn-secondary, .sn-btn-cancel, .sn-btn-danger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
          text-decoration: none;
        }
        .sn-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .sn-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .sn-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .sn-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .sn-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        .sn-btn-danger:hover:not(:disabled) {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .sn-btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .sn-btn-cancel {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .sn-btn-cancel:hover {
          background: #f1f5f9;
        }
        .sn-btn-flex {
          flex: 1;
        }
        .sn-dropdown-menu {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1);
          padding: 6px;
          position: absolute;
          z-index: 1000;
        }
        .sn-dropdown-menu .dropdown-item {
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
        }
        .sn-dropdown-menu .dropdown-item:hover {
          background: #f1f5f9;
        }

        /* ========== ALERTS ========== */
        .sn-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .sn-alert > i {
          font-size: 1.3rem;
          flex-shrink: 0;
        }
        .sn-alert-warning {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          color: #92400e;
          border-left: 4px solid #f59e0b;
        }

        /* ========== STATS ========== */
        .sn-stat-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .sn-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .sn-stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .sn-stat-icon-red   { background: #fee2e2; color: #ef4444; }
        .sn-stat-icon-amber { background: #fef3c7; color: #f59e0b; }
        .sn-stat-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .sn-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .sn-stat-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .sn-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ========== FILTRES ========== */
        .sn-filters {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
        }
        .sn-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .sn-search-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
        }
        .sn-search-input {
          width: 100%;
          padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          background: #f8fafc;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .sn-search-input:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .sn-search-clear {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1rem;
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .sn-search-clear:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        .sn-select {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #f8fafc;
          color: #334155;
          outline: none;
          cursor: pointer;
          font-family: inherit;
        }
        .sn-select:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .sn-filter-info {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 11px 16px;
          background: #f1f5f9;
          border-radius: 10px;
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 500;
        }
        .sn-filter-info strong {
          color: #ef4444;
        }

        /* ========== TABLE ========== */
        .sn-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .sn-table {
          font-size: 0.875rem;
        }
        .sn-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 16px;
          border-bottom: 1px solid #e2e8f0;
          border-top: none;
          white-space: nowrap;
        }
        .sn-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .sn-row {
          transition: background-color 0.15s;
        }
        .sn-row:hover {
          background-color: #f8fafc;
        }
        .sn-sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .sn-sortable-header:hover {
          color: #ef4444;
        }
        .sn-sortable-content {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .sn-sort-icon {
          color: #ef4444;
          font-size: 0.75rem;
        }

        .sn-checkbox-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sn-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid #cbd5e1;
          cursor: pointer;
          accent-color: #ef4444;
        }

        .sn-code {
          font-size: 0.78rem;
          color: #991b1b;
          background: #fee2e2;
          padding: 4px 10px;
          border-radius: 6px;
          font-weight: 700;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }

        .sn-name-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .sn-avatar {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
          flex-shrink: 0;
        }
        .sn-name-main {
          font-weight: 700;
          color: #0f172a;
        }

        .sn-reason-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          border-radius: 8px;
          font-size: 0.72rem;
          font-weight: 600;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #991b1b;
          border: 1px solid #fecaca;
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .sn-reason-badge > i {
          font-size: 0.8rem;
          flex-shrink: 0;
        }

        .sn-date-cell {
          color: #64748b;
          font-size: 0.78rem;
          font-variant-numeric: tabular-nums;
        }

        .sn-actions {
          display: inline-flex;
          gap: 6px;
          justify-content: flex-end;
        }
        .sn-action-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.9rem;
        }
        .sn-action-delete:hover {
          background: #ef4444;
          border-color: #ef4444;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .sn-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .sn-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #ef4444;
          border-radius: 50%;
          animation: sn-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .sn-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .sn-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .sn-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #d1fae5;
          color: #10b981;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .sn-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .sn-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .sn-btn-empty-reset {
          display: inline-flex;
          align-items: center;
          background: white;
          color: #ef4444;
          border: 1px solid #fecaca;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .sn-btn-empty-reset:hover {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
        }

        /* ========== PAGINATION ========== */
        .sn-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .sn-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .sn-pagination-info strong {
          color: #0f172a;
        }
        .sn-pagination-selected {
          color: #ef4444;
          margin-left: 8px;
        }
        .sn-pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .sn-page-btn {
          min-width: 34px;
          height: 34px;
          padding: 0 10px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .sn-page-btn:hover:not(:disabled) {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
        }
        .sn-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .sn-page-btn.active {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
        }

        /* ========== MODAL ========== */
        .sn-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .sn-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          color: white;
        }
        .sn-modal-header-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        }
        .sn-modal-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .sn-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0;
        }
        .sn-modal-subtitle {
          color: rgba(255,255,255,0.85);
          font-size: 0.8rem;
        }
        .sn-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1rem;
        }
        .sn-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .sn-modal-body {
          padding: 24px;
          background: #f8fafc;
          max-height: 65vh;
          overflow-y: auto;
        }
        .sn-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        /* Warning box */
        .sn-warning-box {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 18px;
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          color: #92400e;
          border-radius: 12px;
          border-left: 4px solid #f59e0b;
          font-size: 0.82rem;
        }
        .sn-warning-box > i {
          font-size: 1.3rem;
          color: #f59e0b;
          flex-shrink: 0;
        }

        /* Form */
        .sn-form-group {
          margin-bottom: 18px;
        }
        .sn-form-label {
          display: block;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.82rem;
          margin-bottom: 8px;
        }
        .sn-required {
          color: #ef4444;
        }
        .sn-optional {
          color: #94a3b8;
          font-weight: 500;
          font-size: 0.75rem;
        }
        .sn-input, .sn-select-full, .sn-textarea {
          width: 100%;
          padding: 11px 14px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .sn-input:focus, .sn-select-full:focus, .sn-textarea:focus {
          border-color: #ef4444;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
        }
        .sn-textarea {
          resize: vertical;
          line-height: 1.5;
        }
        .sn-form-hint {
          margin-top: 6px;
          font-size: 0.72rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .sn-form-hint i {
          color: #ef4444;
        }

        /* Delete modal */
        .sn-delete-body {
          padding: 32px 24px 24px;
          text-align: center;
        }
        .sn-delete-icon-wrapper {
          margin-bottom: 20px;
        }
        .sn-delete-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          box-shadow: 0 12px 24px -8px rgba(220, 38, 38, 0.3);
        }
        .sn-delete-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 12px;
          font-size: 1.25rem;
        }
        .sn-delete-text {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 0.875rem;
        }
        .sn-delete-item {
          display: flex;
          align-items: center;
          gap: 14px;
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 16px;
          border: 1px solid #e2e8f0;
          text-align: left;
        }
        .sn-delete-item-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .sn-delete-item-content {
          min-width: 0;
          flex-grow: 1;
        }
        .sn-delete-item-label {
          font-size: 0.72rem;
          color: #94a3b8;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .sn-delete-item-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 1rem;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          word-break: break-all;
        }
        .sn-delete-warning {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: #fef2f2;
          color: #dc2626;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 700;
          margin-bottom: 20px;
        }
        .sn-delete-actions {
          display: flex;
          gap: 10px;
        }

        /* ========== ANIMATIONS ========== */
        .sn-spin {
          animation: sn-spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes sn-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .sn-header {
            padding: 20px;
          }
          .sn-header-title {
            font-size: 1.2rem;
          }
          .sn-pagination {
            flex-direction: column;
          }
          .sn-stat-value {
            font-size: 1.25rem;
          }
          .sn-modal-body {
            padding: 16px;
          }
          .sn-delete-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}